// Toolkits (02-DESIGN/08-toolkits/01-the-toolkit-contract.md § discovering
// and calling from a generic client; 03-linking.md § discovering what needs
// linking; 01-the-console.md § the surfaces): what the workspace offers from
// discovery and, for this person, whether each is linked, configured or not
// yet, with the one act the scheme derives in the row menu; add, and remove
// in the row menu, for an admin. A
// runtime registered but not serving yet is listed as starting, with no
// tools. The add and *Connect* are forms in the page's place
// (01-the-console.md § conventions: a form).

import { Plus } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";

import { tenantPath } from "../../address/address.ts";
import { Button, Empty, NotAllowed, Notice, PageHead, Pending, Pill, plural } from "../../ui/components.tsx";
import { FormPage, useFormState } from "../../ui/form.tsx";
import { Table, type Column, type MenuEntry } from "../../ui/table.tsx";
import { useSurface } from "../surface.tsx";
import { plainState } from "../workloads/state.ts";
import { connectForm, connectingOf, ConnectRecord, useToolkitMenu, whyNot } from "./Acts.tsx";
import { AddToolkit } from "./AddToolkit.tsx";
import { removeMenu, removeToolkit } from "./remove.ts";
import { scopeWords, standingOf, standingWords } from "./state.ts";
import { useToolkits, type Offered } from "./useToolkits.ts";

export function ToolkitsPage() {
  const s = useSurface();
  const navigate = useNavigate();
  const t = useToolkits();
  const forms = useFormState();
  const [err, setErr] = useState<unknown>(undefined);
  const manage = s.offered("toolkits:manage");
  const blocked = whyNot(s.principal, t.noVault, s.name, t.vault);

  const add = manage ? (
    <Button
      kind="primary"
      disabled={t.image === undefined}
      onClick={() => {
        forms.show("add");
      }}
    >
      <Plus strokeWidth={1.8} aria-hidden="true" />
      Add a toolkit
    </Button>
  ) : undefined;
  const linked = t.linked.state === "ready" ? t.linked.value : new Set<string>();
  const acts = useToolkitMenu({
    noVault: t.noVault,
    onChanged: t.reload,
    onError: setErr,
    onConnect: (tk) => {
      forms.show(connectForm(tk.name));
    },
  });
  const status = (o: Offered) => {
    if (o.toolkit === undefined) {
      const p = o.workload === undefined ? undefined : plainState(o.workload);
      return { tone: p?.tone ?? ("info" as const), word: p?.word ?? "starting" };
    }
    return standingWords(standingOf(o.toolkit, linked, s.principal));
  };
  // An admin removes a toolkit from its row as from its page, last, asked
  // first (01-the-console.md § conventions: a row's actions; remove).
  const removeEntry = (o: Offered): MenuEntry[] => {
    const workload = o.toolkit?.workload ?? o.workload?.id;
    if (!manage || workload === undefined) {
      return [];
    }
    return removeMenu(o.toolkit?.name ?? o.name, () => {
      setErr(undefined);
      removeToolkit(s.bus, workload).then(t.reload, (e: unknown) => {
        setErr(e);
        t.reload();
      });
    });
  };
  const columns: Column<Offered>[] = [
    {
      key: "toolkit",
      header: "Toolkit",
      width: "minmax(0, 1.6fr)",
      value: (o) => o.toolkit?.name ?? o.name,
      cell: (o) => (
        <span className="cell">
          <span className="cell-main">{o.toolkit?.name ?? o.name}</span>
          <span className="cell-sub">
            {o.toolkit === undefined ? "no tools yet" : plural(o.toolkit.tools.length, "tool")}
          </span>
        </span>
      ),
    },
    {
      key: "acts",
      header: "Acts as",
      width: "130px",
      facet: "Acts as",
      value: (o) => (o.toolkit === undefined ? "—" : scopeWords(o.toolkit.scope, s.name)),
      cell: (o) =>
        o.toolkit === undefined ? <span className="faint">—</span> : scopeWords(o.toolkit.scope, s.name),
    },
    {
      key: "status",
      header: "Status",
      width: "140px",
      facet: "Status",
      value: (o) => status(o).word,
      cell: (o) => {
        const st = status(o);
        return <Pill tone={st.tone}>{st.word}</Pill>;
      },
    },
  ];

  if (forms.open === "add" && manage) {
    return t.image === undefined ? (
      <FormPage title="Add a toolkit" back="Toolkits" onClose={forms.close}>
        <Notice>This install publishes no image for a toolkit, so none can be added from here.</Notice>
      </FormPage>
    ) : (
      <AddToolkit
        image={t.image}
        onClose={forms.close}
        onAdded={(name) => {
          t.reload();
          void navigate(tenantPath(s.tenant, "toolkits", name), { replace: true });
        }}
      />
    );
  }
  const connecting = connectingOf(forms.open);
  const connected =
    connecting === undefined || t.offered.state !== "ready"
      ? undefined
      : t.offered.value.find((o) => o.toolkit?.name === connecting)?.toolkit;
  if (connected !== undefined) {
    return (
      <ConnectRecord
        tk={connected}
        back="Toolkits"
        onClose={forms.close}
        onConnected={() => {
          forms.close();
          t.reload();
        }}
      />
    );
  }

  return (
    <>
      <PageHead
        title="Toolkits"
        line={`What ${s.name} can call on your behalf, and what each needs from you first.`}
        action={add}
      />
      {manage && t.image === undefined && (
        <Notice title="Nothing to add a toolkit with">
          This install publishes no image for a toolkit that wraps a remote MCP server, so none can be added
          from here.
        </Notice>
      )}
      {blocked !== undefined && t.offered.state === "ready" && t.offered.value.length > 0 && (
        <Notice>{blocked}</Notice>
      )}
      {err !== undefined && <NotAllowed err={err} />}
      {t.offered.state === "pending" && <Pending what="Looking for toolkits" />}
      {t.offered.state === "refused" && <NotAllowed err={t.offered.err} what="Toolkits" />}
      {t.linked.state === "refused" && !t.noVault && (
        <NotAllowed err={t.linked.err} what="What you have linked" />
      )}
      {t.offered.state === "ready" && t.offered.value.length === 0 && (
        <Empty action={add}>
          {manage
            ? "No toolkits yet. Add one that wraps a remote MCP server, and its tools can be called as you or as the workspace."
            : `${s.name} offers no toolkits yet. An admin adds them.`}
        </Empty>
      )}
      {t.offered.state === "ready" && t.offered.value.length > 0 && (
        <Table
          label="Toolkits"
          rows={t.offered.value}
          rowKey={(o) => (o.toolkit === undefined ? `w/${o.name}` : o.toolkit.workload)}
          sort={{ key: "toolkit", dir: "asc" }}
          // A row opens its toolkit's page, serving or not: one not serving yet says so there, its workload linked.
          to={(o) => tenantPath(s.tenant, "toolkits", o.toolkit?.name ?? o.name)}
          columns={columns}
          menu={(o) => [
            ...(o.toolkit !== undefined && s.offered("toolkits:link")
              ? acts.menu(o.toolkit, standingOf(o.toolkit, linked, s.principal))
              : []),
            ...removeEntry(o),
          ]}
        />
      )}
    </>
  );
}
